/**
 * React Query hooks for WL-4 (fieldiq-api#1345): the Wireline & Slickline rate
 * card, a rent-in's billing rate, and field tickets — the Well Servicing hooks
 * (useWlTickets.ts) carried over, since this vertical's API mirrors WS-4.
 *
 * Same rules as useWirelineSlickline.ts: every key starts
 * ['wirelineSlickline'] and every write invalidates that one prefix (a line
 * moves the ticket, the register's total and the job's Tickets tab); every
 * read spreads LOAD_FAILURE_QUERY_OPTIONS so a refusal reads as a refusal,
 * never as an empty list.
 *
 * A write that can return a signed ticket to draft resolves to
 * `{ ticket, reverted }` so the screen can say so — `reverted_to_draft` is
 * never dropped on the way.
 */
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { LOAD_FAILURE_QUERY_OPTIONS } from '../lib/load-failure';
import { putToPresignedUrl } from '../api/upload';
import { invalidateWl, unwrapWl, unwrapWlList } from './useWirelineSlickline';
import type {
  WlRate,
  WlRateWrite,
  WlRentInBilling,
  WlTicket,
  WlTicketEdit,
  WlTicketHeaderWrite,
  WlTicketLineCreate,
  WlTicketLinePatch,
  WlTicketRecipient,
  WlTicketSendResult,
} from '../types/wireline-slickline-tickets';

type Params = Record<string, string | undefined>;
const clean = (p?: Params) => {
  if (!p) return undefined;
  const out: Record<string, string> = {};
  Object.entries(p).forEach(([k, v]) => {
    if (v) out[k] = v;
  });
  return Object.keys(out).length ? out : undefined;
};

export const wlTicketKeys = {
  rates: (p?: Params) => ['wirelineSlickline', 'rates', clean(p) ?? {}] as const,
  rate: (id: string) => ['wirelineSlickline', 'rate', id] as const,
  rentInBilling: (id: string) => ['wirelineSlickline', 'rentInBilling', id] as const,
  tickets: (p?: Params) => ['wirelineSlickline', 'tickets', clean(p) ?? {}] as const,
  ticket: (id: string) => ['wirelineSlickline', 'ticket', id] as const,
  recipient: (id: string) => ['wirelineSlickline', 'ticketRecipient', id] as const,
};

function useWlWrite<V, R>(fn: (v: V) => Promise<R>) {
  const qc = useQueryClient();
  return useMutation({ mutationFn: fn, onSuccess: () => invalidateWl(qc) });
}

/**
 * A delete: drop the deleted record's own query and do NOT wait on the refetch.
 * Awaiting invalidateWl here refetched the gone record (404, with retries) for
 * ~4s before the screen's onSuccess could navigate away — measured on Pipeline & Facility's PFT-0003.
 */
function useWlDelete(fn: (id: string) => Promise<unknown>, key: (id: string) => readonly unknown[]) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: (_data, id) => {
      qc.removeQueries({ queryKey: key(id), exact: true });
      void invalidateWl(qc);
    },
  });
}

/** A ticket write's echo: the ticket as it now is, and whether it went back to draft. */
export interface WlTicketEditResult {
  ticket: WlTicket | null;
  reverted: boolean;
}
const editResult = (body: WlTicketEdit | unknown): WlTicketEditResult => ({
  ticket: unwrapWl<WlTicket>(body).data,
  reverted: !!(body && typeof body === 'object' && (body as { reverted_to_draft?: unknown }).reverted_to_draft === true),
});

// ─── Rate card ───────────────────────────────────────────────────────────────

export interface WlRateFilters extends Params {
  /** A customer's uuid, or 'default'. */
  customer_id?: string;
  kind?: string;
  on?: string;
}

export function useWlRates(filters?: WlRateFilters, enabled = true) {
  return useQuery({
    queryKey: wlTicketKeys.rates(filters),
    queryFn: async () => unwrapWlList<WlRate>((await api.wirelineSlickline.rates.list(clean(filters))).data),
    enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useWlRate(id: string | undefined) {
  return useQuery({
    queryKey: wlTicketKeys.rate(id ?? ''),
    queryFn: async () => unwrapWl<WlRate>((await api.wirelineSlickline.rates.get(id as string)).data),
    enabled: !!id,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export const useCreateWlRate = () =>
  useWlWrite(async (body: WlRateWrite) => unwrapWl<WlRate>((await api.wirelineSlickline.rates.create(body)).data));
export const useUpdateWlRate = () =>
  useWlWrite(async ({ id, body }: { id: string; body: WlRateWrite }) =>
    unwrapWl<WlRate>((await api.wirelineSlickline.rates.update(id, body)).data));
export const useDeleteWlRate = () => useWlDelete(async (id: string) => (await api.wirelineSlickline.rates.remove(id)).data, wlTicketKeys.rate);

// ─── A rent-in's billing rate ────────────────────────────────────────────────

export function useWlRentInBillingRates(rentInIds: string[], enabled = true) {
  return useQueries({
    queries: rentInIds.map((id) => ({
      queryKey: wlTicketKeys.rentInBilling(id),
      queryFn: async () => unwrapWl<WlRentInBilling>((await api.wirelineSlickline.rentInBilling.get(id)).data),
      enabled,
      ...LOAD_FAILURE_QUERY_OPTIONS,
    })),
  });
}

export const useSetWlRentInBillingRate = () =>
  useWlWrite(async ({ rentInId, rateId }: { rentInId: string; rateId: string | null }) =>
    unwrapWl<WlRentInBilling>((await api.wirelineSlickline.rentInBilling.set(rentInId, rateId)).data));

// ─── Tickets ─────────────────────────────────────────────────────────────────

export interface WlTicketFilters extends Params {
  job_id?: string;
  customer_id?: string;
  unit_id?: string;
  status?: string;
}

export function useWlTickets(filters?: WlTicketFilters, enabled = true) {
  return useQuery({
    queryKey: wlTicketKeys.tickets(filters),
    queryFn: async () => unwrapWlList<WlTicket>((await api.wirelineSlickline.tickets.list(clean(filters))).data),
    enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useWlTicket(id: string | undefined) {
  return useQuery({
    queryKey: wlTicketKeys.ticket(id ?? ''),
    queryFn: async () => unwrapWl<WlTicket>((await api.wirelineSlickline.tickets.get(id as string)).data),
    enabled: !!id,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

/** Who a send would address — the office's write role only (the API gates it as a write). */
export function useWlTicketRecipient(id: string | undefined, enabled = true) {
  return useQuery({
    queryKey: wlTicketKeys.recipient(id ?? ''),
    queryFn: async () => unwrapWl<WlTicketRecipient>((await api.wirelineSlickline.tickets.recipient(id as string)).data),
    enabled: !!id && enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

/** Per day = per unit per day: `unitId` is sent when the job runs more than one unit (else 400 `unit_id_required`). */
export const useCreateWlTicket = () =>
  useWlWrite(async ({ jobId, workDate, unitId }: { jobId: string; workDate?: string; unitId?: string }) => {
    const body: { work_date?: string; unit_id?: string } = {};
    if (workDate) body.work_date = workDate;
    if (unitId) body.unit_id = unitId;
    return unwrapWl<WlTicket>((await api.wirelineSlickline.tickets.create(jobId, body)).data);
  });

/** Refresh answers `refreshed.reverted` rather than `reverted_to_draft`; both reach the screen as `reverted`. */
export const useRefreshWlTicket = () =>
  useWlWrite(async (id: string): Promise<WlTicketEditResult> => {
    const body = (await api.wirelineSlickline.tickets.refresh(id)).data as { refreshed?: { reverted?: unknown } } | undefined;
    return { ticket: unwrapWl<WlTicket>(body).data, reverted: body?.refreshed?.reverted === true };
  });

export const useUpdateWlTicket = () =>
  useWlWrite(async ({ id, body }: { id: string; body: WlTicketHeaderWrite }) =>
    editResult((await api.wirelineSlickline.tickets.update(id, body)).data));

export const useDeleteWlTicket = () => useWlDelete(async (id: string) => (await api.wirelineSlickline.tickets.remove(id)).data, wlTicketKeys.ticket);

export const useAddWlTicketLine = () =>
  useWlWrite(async ({ id, body }: { id: string; body: WlTicketLineCreate }) =>
    editResult((await api.wirelineSlickline.tickets.addLine(id, body)).data));

export const useUpdateWlTicketLine = () =>
  useWlWrite(async ({ id, lineId, body }: { id: string; lineId: string; body: WlTicketLinePatch }) =>
    editResult((await api.wirelineSlickline.tickets.updateLine(id, lineId, body)).data));

export const useRemoveWlTicketLine = () =>
  useWlWrite(async ({ id, lineId }: { id: string; lineId: string }) =>
    editResult((await api.wirelineSlickline.tickets.removeLine(id, lineId)).data));

export const useOfficeApproveWlTicket = () =>
  useWlWrite(async (id: string) => unwrapWl<WlTicket>((await api.wirelineSlickline.tickets.officeApprove(id)).data));

export const useSendWlTicket = () =>
  useWlWrite(async ({ id, email, cc }: { id: string; email?: string; cc?: string[] }): Promise<WlTicketSendResult> => {
    const body: { email?: string; cc?: string[] } = {};
    if (email) body.email = email;
    if (cc && cc.length) body.cc = cc;
    return (await api.wirelineSlickline.tickets.send(id, body)).data;
  });

/**
 * Upload the photo of the signed paper ticket through the presigned flow:
 * presign a `wl_ticket` upload against this ticket, PUT the file to storage
 * (putToPresignedUrl throws unless storage answered 2xx), confirm, and return
 * the upload id. No multipart body is built, so no Content-Type is set by hand
 * on an API call. Online only: the offline upload queue has no step four.
 */
export async function uploadWlTicketFile(ticketId: string, file: File): Promise<string> {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    throw new Error('You are offline. The signed ticket can only be attached with a connection — try again when you have signal.');
  }
  const contentType = file.type || 'application/octet-stream';
  const presign = await api.uploads.getPresignedUrl({
    fileName: file.name || 'signed-ticket',
    contentType,
    entityType: 'wl_ticket',
    entityId: ticketId,
  });
  // The uploads row is minted at presign (`uploadId`); confirm answers only the size.
  const p = ((presign.data as { data?: unknown })?.data ?? presign.data) as { uploadUrl?: unknown; key?: unknown; uploadId?: unknown };
  if (typeof p?.uploadUrl !== 'string' || typeof p?.key !== 'string' || typeof p?.uploadId !== 'string') {
    throw new Error('The upload could not be prepared. Try again.');
  }
  await putToPresignedUrl(p.uploadUrl, file, contentType);
  await api.uploads.confirm({ key: p.key, size: file.size });
  return p.uploadId;
}

/** Every step throws, so nothing reads as recorded unless the last POST answered 2xx. Once only: 409 `approval_already_recorded`. */
export const useRecordWlTicketApproval = () =>
  useWlWrite(async ({ id, file, approvedByName, approvedOn }: { id: string; file: File; approvedByName: string; approvedOn: string }) => {
    const uploadId = await uploadWlTicketFile(id, file);
    return unwrapWl<WlTicket>(
      (await api.wirelineSlickline.tickets.recordApproval(id, { upload_id: uploadId, approved_by_name: approvedByName, approved_on: approvedOn })).data,
    );
  });

/** Void keeps the ticket on file and stops it billing; the reason is required (400 `reason_required`). */
export const useVoidWlTicket = () =>
  useWlWrite(async ({ id, reason }: { id: string; reason: string }) =>
    unwrapWl<WlTicket>((await api.wirelineSlickline.tickets.void(id, reason)).data));
